import { ProjectTask } from '@/types';

/**
 * Who may move or edit a project task. This mirrors
 * `App\Support\ProjectTaskDepartmentAuthorization::canUpdate()` — keep the two
 * in step, otherwise the UI offers an action the endpoint rejects with a 403.
 */

/**
 * Why an update is denied: `timeline` (the project timeline is owner/sales
 * only), `department` (the task belongs to another department) or
 * `no_permission` (no task rights at all). `null` means it is allowed.
 */
export type TaskUpdateDenialReason = 'timeline' | 'department' | 'no_permission';

export type TaskUpdateSubject = {
    /** Role names from `auth.roles`. */
    roles: string[];
    /** Permission slugs from `auth.abilities`. */
    abilities: string[];
    /** UUID of the signed-in user, used for the assignee exception. */
    userId?: string | null;
    /** The signed-in user is the responsible person for the owning project. */
    isProjectOwner?: boolean;
};

export type TaskUpdateTarget = Pick<ProjectTask, 'is_project_timeline' | 'movable_by_roles' | 'assigned_to'>;

export function taskUpdateDenialReason(task: TaskUpdateTarget | null | undefined, subject: TaskUpdateSubject): TaskUpdateDenialReason | null {
    if (!task) {
        return 'no_permission';
    }

    const { roles, abilities, userId = null, isProjectOwner = false } = subject;

    if (roles.includes('Administrator')) {
        return null;
    }

    if (task.is_project_timeline) {
        return roles.includes('Sales Manager') || isProjectOwner ? null : 'timeline';
    }

    if (!abilities.includes('tasks.update')) {
        return 'no_permission';
    }

    // The person the task is assigned to may move it regardless of department.
    const assignedTo = typeof task.assigned_to === 'string' ? task.assigned_to.trim() : '';
    if (assignedTo.length > 0 && userId && assignedTo === userId) {
        return null;
    }

    const allowedRoles = Array.isArray(task.movable_by_roles) ? task.movable_by_roles.filter((role) => role.trim().length > 0) : [];

    // Backward compatibility for old tasks: no department restrictions.
    if (allowedRoles.length === 0) {
        return null;
    }

    return allowedRoles.some((role) => roles.includes(role)) ? null : 'department';
}

export function canUpdateTask(task: TaskUpdateTarget | null | undefined, subject: TaskUpdateSubject): boolean {
    return taskUpdateDenialReason(task, subject) === null;
}
